import { useState } from 'react';
import type {
    CommissionRecord,
    SellerCommissionForecast,
    SellerCommissionForecastMonth,
    SellerProfileMonth,
} from './types';
import { fmtMonthShort } from './CommissionExecutiveCharts';
import { Link } from 'react-router-dom';
import ForecastMonthDetailModal from './ForecastMonthDetailModal';

const brl = (v: number) =>
    new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL', maximumFractionDigits: 0 }).format(v ?? 0);

const brlCompact = (v: number) => {
    if (v >= 1_000_000) return `R$ ${(v / 1_000_000).toFixed(1)}M`;
    if (v >= 1_000) return `R$ ${(v / 1_000).toFixed(0)}k`;
    return brl(v);
};

const STATUS_COLORS: Record<string, string> = {
    paid: '#10b981',
    nf_submitted: '#3b82f6',
    awaiting_nf: '#f59e0b',
    pending: '#9ca3af',
    cancelled: '#ef4444',
};

function scaleY(value: number, max: number, height: number) {
    if (max <= 0) return height;
    return height - (value / max) * height;
}

function Panel({ title, subtitle, children, className }: {
    title: string;
    subtitle?: string;
    children: React.ReactNode;
    className?: string;
}) {
    return (
        <section className={`rounded-2xl border border-gray-200 bg-white p-4 dark:border-zinc-700 dark:bg-zinc-900 ${className ?? ''}`}>
            <div className="mb-3">
                <h3 className="text-sm font-semibold text-gray-900 dark:text-white">{title}</h3>
                {subtitle && <p className="text-xs text-gray-500">{subtitle}</p>}
            </div>
            {children}
        </section>
    );
}

/**
 * Previsão de 12 meses — grade visual (sem tabela).
 */
export function SellerCommissionForecastChart({
    forecast,
}: {
    forecast: SellerCommissionForecast | null | undefined;
}) {
    const [selectedMonth, setSelectedMonth] = useState<SellerCommissionForecastMonth | null>(null);
    const months = forecast?.months ?? [];
    const horizon = forecast?.horizon_months ?? 12;
    const horizonTotal = forecast?.horizon_total ?? months.reduce((s, m) => s + m.amount, 0);
    const totalAll = forecast?.total_remaining ?? horizonTotal;
    const installmentsCount = forecast?.horizon_installments_count ?? forecast?.installments_count ?? 0;
    const beyondHorizon = Math.max(0, totalAll - horizonTotal);

    if (!forecast || months.length === 0) {
        return (
            <div className="rounded-2xl border border-dashed border-gray-200 bg-gray-50/80 px-6 py-12 text-center dark:border-zinc-700 dark:bg-zinc-800/40">
                <p className="text-sm font-medium text-gray-600 dark:text-zinc-300">
                    Nenhuma parcela no cronograma
                </p>
                <p className="mt-1 text-xs text-gray-400">
                    Quando houver vencimentos, a projeção dos próximos 12 meses aparece aqui.
                </p>
            </div>
        );
    }

    const maxAmount = Math.max(...months.map((m) => m.amount), 0);
    const hasAny = maxAmount > 0;

    return (
        <div className="space-y-5">
            {/* Resumo compacto */}
            <div className="flex flex-col gap-4 rounded-2xl bg-gradient-to-br from-orange-50 via-white to-sky-50 p-5 ring-1 ring-orange-100/80 dark:from-orange-950/30 dark:via-zinc-900 dark:to-sky-950/20 dark:ring-orange-900/40 sm:flex-row sm:items-end sm:justify-between">
                <div>
                    <p className="text-[11px] font-semibold uppercase tracking-wider text-orange-700/80 dark:text-orange-400">
                        Horizonte de {horizon} meses · sua comissão
                    </p>
                    <p className="mt-1 text-3xl font-black tracking-tight text-gray-900 dark:text-white">
                        {brl(horizonTotal)}
                    </p>
                    <p className="mt-1 text-xs text-gray-500">
                        {installmentsCount} parcela{installmentsCount === 1 ? '' : 's'}
                        {beyondHorizon > 0 && (
                            <span className="text-sky-700 dark:text-sky-400">
                                {' '}· + {brlCompact(beyondHorizon)} depois
                            </span>
                        )}
                    </p>
                    <p className="mt-1 text-[11px] text-gray-400">
                        Valor = comissão calculada (parcela × taxa), não o valor da parcela do cliente. Clique no mês para detalhar.
                    </p>
                </div>
                {beyondHorizon > 0 && (
                    <div className="rounded-xl bg-white/70 px-4 py-2.5 text-right shadow-sm ring-1 ring-gray-100 dark:bg-zinc-900/60 dark:ring-zinc-700">
                        <p className="text-[10px] font-semibold uppercase tracking-wide text-gray-400">Total geral</p>
                        <p className="text-lg font-bold text-gray-900 dark:text-white">{brl(totalAll)}</p>
                    </div>
                )}
            </div>

            {/* Grade de meses */}
            <div className="grid grid-cols-2 gap-2.5 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6">
                {months.map((m, i) => {
                    const isCurrent = i === 0;
                    const intensity = maxAmount > 0 ? m.amount / maxAmount : 0;
                    const barPct = Math.max(m.amount > 0 ? 8 : 0, intensity * 100);
                    const clickable = m.amount > 0;

                    return (
                        <button
                            key={m.month}
                            type="button"
                            disabled={!clickable}
                            onClick={() => clickable && setSelectedMonth(m)}
                            className={[
                                'group relative flex flex-col overflow-hidden rounded-2xl border p-3 text-left transition',
                                isCurrent
                                    ? 'border-amber-300 bg-amber-50/90 shadow-sm dark:border-amber-700/60 dark:bg-amber-950/30'
                                    : m.amount > 0
                                        ? 'cursor-pointer border-orange-100 bg-white hover:border-orange-300 hover:shadow-md dark:border-zinc-700 dark:bg-zinc-900 dark:hover:border-orange-700'
                                        : 'cursor-default border-gray-100 bg-gray-50/60 dark:border-zinc-800 dark:bg-zinc-900/40',
                            ].join(' ')}
                        >
                            <div className="mb-2 flex items-start justify-between gap-1">
                                <span className="text-xs font-semibold capitalize text-gray-800 dark:text-zinc-100">
                                    {fmtMonthShort(m.month)}
                                </span>
                                {isCurrent && (
                                    <span className="shrink-0 rounded-full bg-amber-200/90 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wide text-amber-900 dark:bg-amber-800 dark:text-amber-100">
                                        agora
                                    </span>
                                )}
                            </div>

                            <p
                                className={[
                                    'text-sm font-bold tabular-nums',
                                    m.amount > 0
                                        ? 'text-orange-700 dark:text-orange-300'
                                        : 'text-gray-300 dark:text-zinc-600',
                                ].join(' ')}
                            >
                                {m.amount > 0 ? brlCompact(m.amount) : '—'}
                            </p>

                            <p className="mt-0.5 text-[10px] text-gray-400">
                                {m.installments_count > 0
                                    ? `${m.installments_count} parc.${m.deals_count ? ` · ${m.deals_count} deals` : ''}`
                                    : 'sem parcela'}
                            </p>

                            <div className="mt-3 h-1.5 w-full overflow-hidden rounded-full bg-gray-100 dark:bg-zinc-800">
                                <div
                                    className={[
                                        'h-full rounded-full transition-all',
                                        isCurrent ? 'bg-amber-500' : 'bg-orange-500',
                                    ].join(' ')}
                                    style={{ width: `${barPct}%` }}
                                />
                            </div>

                            {m.amount > 0 && (
                                <p className="mt-1.5 text-[9px] text-blue-600/80 dark:text-blue-400/80">
                                    acum. {brlCompact(m.cumulative)}
                                    <span className="ml-1 text-orange-500 opacity-0 transition group-hover:opacity-100">· ver</span>
                                </p>
                            )}
                        </button>
                    );
                })}
            </div>

            {!hasAny && (
                <p className="text-center text-xs text-gray-400">
                    Sem comissão prevista no cronograma para os próximos {horizon} meses.
                </p>
            )}

            <ForecastMonthDetailModal month={selectedMonth} onClose={() => setSelectedMonth(null)} />
        </div>
    );
}

/** @deprecated Mantido para telas antigas — preferir SellerCommissionForecastChart */
export function SellerMonthlyComboChart({
    months,
}: {
    months: SellerProfileMonth[];
}) {
    const labels = months.map((m) => m.month);
    const paid = months.map((m) => m.paid);
    const accrued = months.map((m) => m.accrued);
    let run = 0;
    const cumulative = months.map((m) => { run += m.paid; return run; });

    const w = 720;
    const h = 260;
    const pad = { t: 20, r: 48, b: 40, l: 56 };
    const chartW = w - pad.l - pad.r;
    const chartH = h - pad.t - pad.b;
    const n = labels.length || 1;
    const maxBar = Math.max(...paid, ...accrued, 1);
    const maxLine = Math.max(...cumulative, 1);
    const groupW = chartW / n;
    const barW = Math.min(16, groupW / 3.5);

    const linePoints = cumulative.map((v, i) => {
        const x = pad.l + groupW * i + groupW / 2;
        const y = pad.t + scaleY(v, maxLine, chartH);
        return `${x},${y}`;
    }).join(' ');

    return (
        <div className="w-full overflow-x-auto">
            <svg viewBox={`0 0 ${w} ${h}`} className="min-w-full w-full" role="img">
                {[0, 0.25, 0.5, 0.75, 1].map((pct) => {
                    const tick = maxBar * pct;
                    const y = pad.t + scaleY(tick, maxBar, chartH);
                    return (
                        <g key={pct}>
                            <line x1={pad.l} y1={y} x2={w - pad.r} y2={y} className="stroke-gray-100 dark:stroke-zinc-800" strokeWidth={1} />
                            <text x={pad.l - 6} y={y + 3} textAnchor="end" className="fill-gray-400 text-[9px]">{brlCompact(tick)}</text>
                        </g>
                    );
                })}
                {labels.map((ym, i) => {
                    const gx = pad.l + groupW * i + groupW / 2;
                    const paidH = (paid[i] / maxBar) * chartH;
                    const accruedH = (accrued[i] / maxBar) * chartH;
                    return (
                        <g key={ym}>
                            <rect x={gx - barW - 1} y={pad.t + chartH - paidH} width={barW} height={paidH} rx={2} className="fill-emerald-500">
                                <title>{`${fmtMonthShort(ym)} Pago: ${brl(paid[i])}`}</title>
                            </rect>
                            <rect x={gx + 1} y={pad.t + chartH - accruedH} width={barW} height={accruedH} rx={2} className="fill-orange-400">
                                <title>{`${fmtMonthShort(ym)} Gerado: ${brl(accrued[i])}`}</title>
                            </rect>
                            <text x={gx} y={h - 10} textAnchor="middle" className="fill-gray-500 text-[9px] capitalize">{fmtMonthShort(ym)}</text>
                        </g>
                    );
                })}
                <polyline points={linePoints} fill="none" className="stroke-blue-600" strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round" />
                {cumulative.map((v, i) => {
                    const x = pad.l + groupW * i + groupW / 2;
                    const y = pad.t + scaleY(v, maxLine, chartH);
                    return <circle key={i} cx={x} cy={y} r={3.5} className="fill-blue-600 stroke-white" strokeWidth={1.5} />;
                })}
            </svg>
            <div className="mt-2 flex flex-wrap justify-center gap-3 text-[10px] text-gray-500">
                <span className="flex items-center gap-1"><span className="size-2.5 rounded-sm bg-emerald-500" /> Pago</span>
                <span className="flex items-center gap-1"><span className="size-2.5 rounded-sm bg-orange-400" /> Gerado</span>
                <span className="flex items-center gap-1"><span className="size-2 rounded-full bg-blue-600" /> Acumulado</span>
            </div>
        </div>
    );
}

/** Linha — tendência de comissão gerada */
export function SellerAccruedTrendChart({ months }: { months: SellerProfileMonth[] }) {
    const recent = months.slice(-12);
    const values = recent.map((m) => m.accrued);
    const max = Math.max(...values, 1);
    const w = 320;
    const h = 100;
    const pad = 8;
    const chartW = w - pad * 2;
    const chartH = h - pad * 2;

    const points = values.map((v, i) => {
        const x = pad + (i / Math.max(values.length - 1, 1)) * chartW;
        const y = pad + scaleY(v, max, chartH);
        return `${x},${y}`;
    }).join(' ');

    const last = values[values.length - 1] ?? 0;
    const prev = values[values.length - 2] ?? 0;
    const trendPct = prev > 0 ? Math.round(((last - prev) / prev) * 100) : null;

    return (
        <div>
            <div className="mb-2 flex items-baseline justify-between">
                <span className="text-lg font-bold text-gray-900 dark:text-white">{brlCompact(last)}</span>
                {trendPct != null && (
                    <span className={`text-xs font-semibold ${trendPct >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>
                        {trendPct >= 0 ? '+' : ''}{trendPct}% vs mês ant.
                    </span>
                )}
            </div>
            <svg viewBox={`0 0 ${w} ${h}`} className="w-full">
                <defs>
                    <linearGradient id="accGrad" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#fb923c" stopOpacity={0.35} />
                        <stop offset="100%" stopColor="#fb923c" stopOpacity={0} />
                    </linearGradient>
                </defs>
                <polygon points={`${pad},${h - pad} ${points} ${w - pad},${h - pad}`} fill="url(#accGrad)" />
                <polyline points={points} fill="none" stroke="#f97316" strokeWidth={2} strokeLinecap="round" />
            </svg>
        </div>
    );
}

/** Barras — deals por mês */
export function SellerDealsBarChart({ months }: { months: SellerProfileMonth[] }) {
    const recent = months.slice(-6);
    const max = Math.max(...recent.map((m) => m.deals), 1);

    return (
        <ul className="space-y-2">
            {recent.map((m) => (
                <li key={m.month} className="flex items-center gap-2 text-xs">
                    <span className="w-12 shrink-0 capitalize text-gray-500">{fmtMonthShort(m.month)}</span>
                    <div className="h-2 flex-1 overflow-hidden rounded-full bg-gray-100 dark:bg-zinc-800">
                        <div className="h-full rounded-full bg-violet-500" style={{ width: `${(m.deals / max) * 100}%` }} />
                    </div>
                    <span className="w-6 shrink-0 text-right font-semibold tabular-nums">{m.deals}</span>
                </li>
            ))}
        </ul>
    );
}

/** Donut — composição por status */
export function SellerStatusDonut({ records }: { records: CommissionRecord[] }) {
    const grouped = records
        .filter((r) => r.status !== 'cancelled')
        .reduce<Record<string, { label: string; amount: number; count: number }>>((acc, r) => {
            if (!acc[r.status]) acc[r.status] = { label: r.status_label, amount: 0, count: 0 };
            acc[r.status].amount += r.final_amount;
            acc[r.status].count += 1;
            return acc;
        }, {});

    const items = Object.entries(grouped).map(([status, v]) => ({ status, ...v }));
    const total = items.reduce((s, i) => s + i.amount, 0);
    const size = 140;
    const r = 50;
    const cx = size / 2;
    const cy = size / 2;
    const circ = 2 * Math.PI * r;

    if (total <= 0) {
        return <p className="py-6 text-center text-xs text-gray-400">Sem comissões registradas.</p>;
    }

    let offset = 0;

    return (
        <div className="flex flex-col items-center gap-3 sm:flex-row sm:items-start">
            <div className="relative shrink-0">
                <svg width={size} height={size}>
                    <circle cx={cx} cy={cy} r={r} fill="none" className="stroke-gray-100 dark:stroke-zinc-800" strokeWidth={20} />
                    {items.map((item) => {
                        const pct = item.amount / total;
                        const dash = pct * circ;
                        const seg = (
                            <circle
                                key={item.status}
                                cx={cx} cy={cy} r={r}
                                fill="none"
                                stroke={STATUS_COLORS[item.status] ?? '#d1d5db'}
                                strokeWidth={20}
                                strokeDasharray={`${dash} ${circ - dash}`}
                                strokeDashoffset={-offset}
                                transform={`rotate(-90 ${cx} ${cy})`}
                            />
                        );
                        offset += dash;
                        return seg;
                    })}
                </svg>
                <div className="absolute inset-0 flex flex-col items-center justify-center">
                    <span className="text-[9px] uppercase text-gray-400">Total</span>
                    <span className="text-xs font-bold">{brlCompact(total)}</span>
                </div>
            </div>
            <ul className="min-w-0 flex-1 space-y-1.5">
                {items.map((item) => (
                    <li key={item.status} className="flex items-center justify-between gap-2 text-[11px]">
                        <span className="flex items-center gap-1.5 truncate">
                            <span className="size-2 shrink-0 rounded-full" style={{ background: STATUS_COLORS[item.status] }} />
                            {item.label}
                        </span>
                        <span className="shrink-0 tabular-nums text-gray-500">{item.count} · {brlCompact(item.amount)}</span>
                    </li>
                ))}
            </ul>
        </div>
    );
}

/** Pipeline vs pago — barras empilhadas mensais */
export function SellerPipelineTrendChart({ months }: { months: SellerProfileMonth[] }) {
    const recent = months.slice(-6);
    const max = Math.max(...recent.map((m) => m.paid + m.pipeline), 1);

    return (
        <ul className="space-y-2.5">
            {recent.map((m) => {
                const paidPct = (m.paid / max) * 100;
                const pipePct = (m.pipeline / max) * 100;
                return (
                    <li key={m.month}>
                        <div className="mb-1 flex justify-between text-[10px]">
                            <span className="capitalize text-gray-600">{fmtMonthShort(m.month)}</span>
                            <span className="text-gray-500">{brlCompact(m.paid + m.pipeline)}</span>
                        </div>
                        <div className="flex h-5 overflow-hidden rounded-md bg-gray-100 dark:bg-zinc-800">
                            {m.paid > 0 && (
                                <div className="bg-emerald-500" style={{ width: `${Math.max(paidPct, 4)}%` }} title={`Pago: ${brl(m.paid)}`} />
                            )}
                            {m.pipeline > 0 && (
                                <div className="bg-amber-400" style={{ width: `${Math.max(pipePct, 4)}%` }} title={`Pipeline: ${brl(m.pipeline)}`} />
                            )}
                        </div>
                    </li>
                );
            })}
            <div className="flex gap-3 pt-1 text-[10px] text-gray-500">
                <span className="flex items-center gap-1"><span className="size-2 rounded bg-emerald-500" /> Pago</span>
                <span className="flex items-center gap-1"><span className="size-2 rounded bg-amber-400" /> Futuro</span>
            </div>
        </ul>
    );
}

export function SellerMonthlyTable({
    months,
    userId,
}: {
    months: SellerProfileMonth[];
    userId?: string | null;
}) {
    const rows = [...months].reverse().slice(0, 8);
    const historyBase = userId
        ? `/commissioning/sellers/${userId}/history`
        : '/my-commission/history';

    return (
        <div className="overflow-x-auto">
            <div className="mb-2 flex items-center justify-between gap-2">
                <p className="text-[10px] text-gray-400">Clique no mês para a timeline detalhada</p>
                <Link
                    to={historyBase}
                    className="text-[10px] font-semibold text-orange-600 hover:underline"
                >
                    Abrir timeline →
                </Link>
            </div>
            <table className="w-full text-[11px]">
                <thead>
                    <tr className="text-left text-gray-500">
                        <th className="pb-2 font-medium">Mês</th>
                        <th className="pb-2 text-right text-emerald-700">Pago</th>
                        <th className="pb-2 text-right text-orange-600">Gerado</th>
                        <th className="pb-2 text-right">Deals</th>
                    </tr>
                </thead>
                <tbody>
                    {rows.map((m) => {
                        const clickable = m.paid > 0 || m.accrued > 0;
                        const cell = (
                            <>
                                <td className="py-2 capitalize font-medium">
                                    {fmtMonthShort(m.month)}
                                    {clickable && (
                                        <span className="ms-1 text-[9px] font-normal text-orange-500">ver</span>
                                    )}
                                </td>
                                <td className="py-2 text-right tabular-nums text-emerald-700">{brlCompact(m.paid)}</td>
                                <td className="py-2 text-right tabular-nums text-orange-600">{brlCompact(m.accrued)}</td>
                                <td className="py-2 text-right tabular-nums">{m.deals}</td>
                            </>
                        );
                        if (!clickable) {
                            return (
                                <tr key={m.month} className="border-t border-gray-50 dark:border-zinc-800">
                                    {cell}
                                </tr>
                            );
                        }
                        return (
                            <tr key={m.month} className="border-t border-gray-50 dark:border-zinc-800 hover:bg-orange-50/70 dark:hover:bg-orange-950/20">
                                <td colSpan={4} className="p-0">
                                    <Link
                                        to={`${historyBase}/${m.month}`}
                                        className="grid grid-cols-4 items-center px-0 py-2 text-inherit"
                                    >
                                        <span className="capitalize font-medium">
                                            {fmtMonthShort(m.month)}
                                            <span className="ms-1 text-[9px] font-normal text-orange-500">ver</span>
                                        </span>
                                        <span className="text-right tabular-nums text-emerald-700">{brlCompact(m.paid)}</span>
                                        <span className="text-right tabular-nums text-orange-600">{brlCompact(m.accrued)}</span>
                                        <span className="text-right tabular-nums">{m.deals}</span>
                                    </Link>
                                </td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        </div>
    );
}

export { Panel as SellerChartPanel };
